import { Fragment, PropsWithChildren, useEffect, useMemo } from 'react';
import { A11Y_HEADING_FOCUS_ID } from '@belong/accessibility';
import { ALIGNMENT } from '@belong/themes';
import { ImageSource, ISectionInfo } from '@belong/types';
import { Button, ButtonGroup, Card, Column, Columns, ContentFormatter, RichText, Section } from '@belong/ui-components';
import { SECTION_INFO_TEST_ID } from '@belong/ui-components/helpers/testIds';
import { Image, ReferenceWrapper } from './styles';

const SectionInfo = ({
  id,
  identifier,
  sectionStyles,
  anchorName,
  bodyAlignment = 'center',
  image,
  dictionary,
  headingContent,
  bodyContent,
  buttonAlignment = ALIGNMENT.center,
  primaryAction,
  secondaryAction,
  primaryControlAction,
  secondaryControlAction,
  withCard,
  cardProps = {},
  imageSize = 'large',
  reference,
  headingPosition = 'bottom',
  headingProps = {},
  bodyProps = {},
  hasContainer = true,
  hasMarginTop = false,
  autoFocusHeading = false,
  ...otherProps
}: ISectionInfo) => {
  useEffect(() => {
    // If flag set, auto focus the heading element for a11y purpose
    if (autoFocusHeading) {
      const el = document.querySelector<HTMLElement>(`#${A11Y_HEADING_FOCUS_ID}`);
      el?.focus();
    }
  }, []);

  const imageSource = useMemo<ImageSource>(() => {
    // No image returns empty image source object
    if (!image) {
      return {};
    }

    if (image.contentType === 'svg') {
      return [image.src, {}];
    }

    return {
      sm: [image.src, { w: 230 }],
      md: [image.src, { w: 345 }]
    };
  }, [image]);

  const primaryIsLoading = !!primaryControlAction?.isLoading;
  const primaryDisabled = !!primaryControlAction?.disabled;
  const primaryOnClick = primaryControlAction?.onClick;

  const secondaryIsLoading = !!secondaryControlAction?.isLoading;
  const secondaryDisabled = !!secondaryControlAction?.disabled;
  const secondaryOnClick = secondaryControlAction?.onClick;

  const ContentWrapper = useMemo(
    () =>
      withCard
        ? ({ children }: PropsWithChildren) => {
            return (
              <Columns alignX="center">
                <Column width={{ lg: '2/3', xl: '1/2' }}>
                  <Card hasSectionPaddingTop {...cardProps}>
                    {children}
                  </Card>
                </Column>
              </Columns>
            );
          }
        : Fragment,
    [withCard, cardProps]
  );

  /**
   * It's possible to use the Heading field to put both the heading and the
   * body content in the same field. This is useful sometimes.
   */
  const hasBodyContent = !!bodyContent;
  const headingId = autoFocusHeading ? A11Y_HEADING_FOCUS_ID : undefined;

  return (
    <Section
      data-contentid={id}
      data-identifier={identifier}
      data-testid={SECTION_INFO_TEST_ID.section}
      hasMarginTop={hasMarginTop}
      hasContainer={hasContainer}
      {...sectionStyles}
      {...otherProps}
      id={anchorName}
      containsCard
    >
      <ContentWrapper>
        <ContentFormatter spacing="large">
          <ContentFormatter spacing="medium">
            {headingPosition === 'top' && (
              <RichText
                {...headingContent}
                hasMargin={{ xs: '0rem 0 1.6rem 0', lg: '0rem 0 2.1rem 0' }}
                alignment="center"
                h3="headingMedium"
                h2="headingMedium"
                h1="headingLarge"
                p="copyLarge"
                id={SECTION_INFO_TEST_ID.heading}
                data-testid={SECTION_INFO_TEST_ID.heading}
                headingId={headingId}
                dictionary={dictionary}
                {...headingProps}
              />
            )}
            {image && <Image size={imageSize} {...image} src={imageSource} />}
            {headingPosition === 'bottom' && (
              <RichText
                {...headingContent}
                hasMargin={{
                  xs: `0rem 0 ${hasBodyContent ? 1.6 : 0}rem 0`,
                  lg: `0rem 0 ${hasBodyContent ? 2.1 : 0}rem 0`
                }}
                alignment="center"
                h3="headingMedium"
                h2="headingMedium"
                h1="headingLarge"
                p="copyLarge"
                id={SECTION_INFO_TEST_ID.heading}
                data-testid={SECTION_INFO_TEST_ID.heading}
                headingId={headingId}
                dictionary={dictionary}
                {...headingProps}
              />
            )}
            {bodyContent && (
              <RichText
                {...bodyContent}
                hasMargin={{ xs: headingPosition === 'top' ? '1rem 0 0 0 0' : '0 0 1rem 0' }}
                dictionary={dictionary}
                alignment={bodyAlignment}
                h3="headingMedium"
                data-testid={SECTION_INFO_TEST_ID.bodyContent}
                {...bodyProps}
              />
            )}
          </ContentFormatter>
          <ReferenceWrapper>{reference}</ReferenceWrapper>

          {(primaryAction ?? secondaryAction) && (
            <ButtonGroup
              hasControl={{
                xs: ['column', 'center', false],
                md: ['row', buttonAlignment, true]
              }}
            >
              {primaryAction && (
                <Button
                  {...primaryAction}
                  data-testid={SECTION_INFO_TEST_ID.primaryButton}
                  isLoading={primaryIsLoading}
                  disabled={primaryDisabled}
                  width={{ xs: 'full', md: 'min' }}
                  onClick={e => {
                    primaryOnClick?.(e);
                  }}
                />
              )}
              {secondaryAction && (
                <Button
                  {...secondaryAction}
                  data-testid={SECTION_INFO_TEST_ID.secondaryButton}
                  isLoading={secondaryIsLoading}
                  disabled={secondaryDisabled}
                  width={{ xs: 'full', md: 'min' }}
                  onClick={e => {
                    secondaryOnClick?.(e);
                  }}
                />
              )}
            </ButtonGroup>
          )}
        </ContentFormatter>
      </ContentWrapper>
    </Section>
  );
};

export default SectionInfo;
